/**
 * React Query hooks for animal groups.
 * React Query hooks for the corresponding entity.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import {
  getColorForIndex,
  prepareAnimalGroupRequest,
  processAnimalGroupResponse,
} from "common/utils/helpers";
import { findIndex } from "lodash";
import { useMemo } from "react";
import { AnimalGroup } from "./animalGroupTypes";
import { Farm } from "./farmTypes";
import { useCurrentFarm } from "./useFarmQueries";

export interface MoveEvent {
  guid: string;
  moveNumber: number;
  fieldGuid: string | null;
  fieldName: string | null;
  dateTimeIn: string | null;
  dateTimeOut: string | null;
  startDryMatter: number | null;
  endDryMatter: number | null;
  movedToShed: boolean;
  kind?: "user" | "machine";
  verificationStatus?: "Proposed" | "Accepted" | "Rejected";
  // null → orphan event found via the field-assignment fallback. The
  // rotation editor renders these as candidate events the farmer can
  // explicitly attach to the group.
  animalGroupGuid: string | null;
}

// Re-export types for convenience
export { animalGroupApi, animalGroupApiV2 };
export type { AnimalGroup };

const animalGroupApi = new API<AnimalGroup>("animalGroups"); // v1 for mutations
const animalGroupApiV2 = new API<AnimalGroup>("animalGroups", "v2"); // v2 for reads

/**
 * Fetch a single animal group by guid
 */
export function useAnimalGroup(guid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.animalGroups.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("Animal group guid is required");
      }
      const response = await animalGroupApiV2.getOne(guid);
      const processed = processAnimalGroupResponse([response]);
      return processed[0];
    },
    enabled: enabled && !!guid,
  });
}

/**
 * Fetch multiple animal groups by their IDs
 */
export function useAnimalGroupsByIds(
  animalGroupIds: string[] | undefined,
  enabled = true
) {
  const queries = useQueries({
    queries: (animalGroupIds || []).map((animalGroupId) => ({
      queryKey: queryKeys.animalGroups.detail(animalGroupId),
      queryFn: async () => {
        const response = await animalGroupApiV2.getOne(animalGroupId);
        const processed = processAnimalGroupResponse([response]);
        return processed[0];
      },
      enabled:
        enabled && animalGroupIds !== undefined && animalGroupIds.length > 0,
    })),
  });

  // Memoize based on dataUpdatedAt to avoid creating new array references
  // when the underlying data hasn't changed (prevents infinite re-render loops)
  const dataKey = queries.map((q) => q.dataUpdatedAt).join(",");
  const animalGroups = useMemo(
    () =>
      queries
        .map((query) => query.data)
        .filter((ag): ag is AnimalGroup => ag !== undefined),
    [dataKey]
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    data: animalGroups,
    isLoading,
    isError,
    queries,
  };
}

/**
 * Get all animal groups from a farm using the farm's animalGroups array
 * Note: This requires the farm to be loaded first with its animalGroups array
 */
export function useAnimalGroups(animalGroupIds: string[] | undefined) {
  return useAnimalGroupsByIds(animalGroupIds);
}

/**
 * Get all animal groups for the current farm.
 * This is a convenience hook that combines useCurrentFarm and useAnimalGroupsByIds.
 * Replaces useAnimalGroupsCompat which used cache reading without subscriptions.
 */
export function useCurrentFarmAnimalGroups() {
  const { data: farm, isLoading: farmLoading } = useCurrentFarm();
  const animalGroupIds = farm?.animalGroups;

  const {
    data: animalGroups,
    isLoading: groupsLoading,
    isError,
    queries,
  } = useAnimalGroupsByIds(animalGroupIds, !!farm);

  return useMemo(
    () => ({
      data: animalGroups,
      isLoading: farmLoading || groupsLoading,
      isError,
      queries,
    }),
    [animalGroups, farmLoading, groupsLoading, isError, queries]
  );
}

/**
 * Create a new animal group
 */
export function useCreateAnimalGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (animalGroup: AnimalGroup) => {
      const postResult = await animalGroupApi.createOne(
        prepareAnimalGroupRequest(animalGroup)
      );
      const newAnimalGroup = await animalGroupApiV2.getOne(postResult.guid);
      const processed = processAnimalGroupResponse([newAnimalGroup]);
      return processed[0];
    },
    onSuccess: async (newAnimalGroup) => {
      // Set the new animal group in cache
      queryClient.setQueryData(
        queryKeys.animalGroups.detail(newAnimalGroup.guid),
        newAnimalGroup
      );

      // Update parent farm's animalGroups array in React Query cache
      if (newAnimalGroup.farm) {
        const farm = queryClient.getQueryData<Farm>(
          queryKeys.farms.detail(newAnimalGroup.farm)
        );
        if (farm && !farm.animalGroups?.includes(newAnimalGroup.guid)) {
          queryClient.setQueryData<Farm>(
            queryKeys.farms.detail(newAnimalGroup.farm),
            {
              ...farm,
              animalGroups: [...(farm.animalGroups || []), newAnimalGroup.guid],
            }
          );
        }
      }

      // Invalidate animal groups list
      await queryClient.invalidateQueries({
        queryKey: queryKeys.animalGroups.all,
      });

      // Invalidate farm query to ensure components that depend on farm.animalGroups re-render
      if (newAnimalGroup.farm) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.farms.detail(newAnimalGroup.farm),
        });
      }
    },
  });
}

/**
 * Update an existing animal group
 */
export function useUpdateAnimalGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (animalGroup: AnimalGroup) => {
      await animalGroupApi.updateOne(prepareAnimalGroupRequest(animalGroup));
      const updatedAnimalGroup = await animalGroupApiV2.getOne(
        animalGroup.guid
      );
      const processed = processAnimalGroupResponse([updatedAnimalGroup]);
      return processed[0];
    },
    onMutate: async (animalGroup) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.animalGroups.detail(animalGroup.guid),
      });

      // Snapshot previous value
      const previousAnimalGroup = queryClient.getQueryData<AnimalGroup>(
        queryKeys.animalGroups.detail(animalGroup.guid)
      );

      // Optimistically update
      if (previousAnimalGroup) {
        queryClient.setQueryData<AnimalGroup>(
          queryKeys.animalGroups.detail(animalGroup.guid),
          { ...previousAnimalGroup, ...animalGroup }
        );
      }

      return { previousAnimalGroup };
    },
    onError: (_err, animalGroup, context) => {
      // Rollback on error
      if (context?.previousAnimalGroup) {
        queryClient.setQueryData(
          queryKeys.animalGroups.detail(animalGroup.guid),
          context.previousAnimalGroup
        );
      }
    },
    onSuccess: async (updatedAnimalGroup) => {
      queryClient.setQueryData(
        queryKeys.animalGroups.detail(updatedAnimalGroup.guid),
        updatedAnimalGroup
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.animalGroups.all,
      });
    },
  });
}

/**
 * Delete an animal group
 */
export function useDeleteAnimalGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (guid: string) => {
      // Get the animal group from cache to find the farm ID before deletion
      const animalGroup = queryClient.getQueryData<AnimalGroup>(
        queryKeys.animalGroups.detail(guid)
      );
      const farmGuid = animalGroup?.farm;

      await animalGroupApi.deleteOne(guid);
      return { guid, farmGuid };
    },
    onSuccess: async ({ guid, farmGuid }) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: queryKeys.animalGroups.detail(guid),
      });

      // Update parent farm's animalGroups array in React Query cache
      if (farmGuid) {
        const farm = queryClient.getQueryData<Farm>(
          queryKeys.farms.detail(farmGuid)
        );
        if (farm) {
          queryClient.setQueryData<Farm>(queryKeys.farms.detail(farmGuid), {
            ...farm,
            animalGroups: (farm.animalGroups || []).filter((id) => id !== guid),
          });
        }
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeys.animalGroups.all,
      });
      // Invalidate fields to update animalGroup references
      await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
    },
  });
}

/**
 * Fetch the move events (rotation history) for a single animal group.
 */
async function fetchAnimalGroupMoveEvents(
  animalGroupId: string
): Promise<MoveEvent[]> {
  const res = await fetch(
    `${
      import.meta.env.VITE_API_ENDPOINT
    }/v3/animalGroups/${animalGroupId}/moveEvents`,
    {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
      },
    }
  );
  if (!res.ok) throw new Error("Failed to fetch move events");
  return (await res.json()) as MoveEvent[];
}

/**
 * Fetch move events (rotation history) for an animal group
 */
export function useAnimalGroupMoveEvents(animalGroupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.animalGroups.moveEvents(animalGroupId ?? ""),
    queryFn: () => fetchAnimalGroupMoveEvents(animalGroupId ?? ""),
    enabled: !!animalGroupId,
  });
}

/**
 * Helper function to get color for an animal group by index
 */
export function getAnimalGroupColorFromArray(
  animalGroups: AnimalGroup[],
  animalGroupId?: string
): string {
  if (!animalGroupId) return getColorForIndex(0);
  const index = findIndex(animalGroups, { guid: animalGroupId });
  return getColorForIndex(index);
}

/**
 * Helper function to get color mapping for all animal groups from array
 */
export function getAnimalGroupColorMappingFromArray(
  animalGroups: AnimalGroup[]
): Record<string, string> {
  const colorByGuid: Record<string, string> = {};
  animalGroups.forEach((ag, index) => {
    colorByGuid[ag.guid] = ag.color ?? getColorForIndex(index);
  });
  return colorByGuid;
}

/**
 * Hook to get color mapping for animal groups
 */
export function useAnimalGroupColorMapping(
  animalGroupIds: string[] | undefined
) {
  const { data: animalGroups = [] } = useAnimalGroupsByIds(animalGroupIds);

  return useMemo(() => {
    return getAnimalGroupColorMappingFromArray(animalGroups);
  }, [animalGroups]);
}

/**
 * Helper function to reset animal groups query cache
 */
export function resetAnimalGroupsQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: queryKeys.animalGroups.all });
}

/**
 * Hook wrapper for resetAnimalGroupsQueryCache
 */
export function useResetAnimalGroupsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetAnimalGroupsQueryCache(queryClient);
}

/**
 * Fetch multiple animal groups by their IDs and populate React Query cache.
 * Used for prefetching during initialization.
 * @param animalGroupIds - Array of animal group IDs to fetch
 * @param queryClient - React Query client instance
 * @returns The fetched and processed animal groups
 */
export async function fetchAnimalGroupsByIdQuery(
  animalGroupIds: string[],
  queryClient: ReturnType<typeof useQueryClient>
): Promise<AnimalGroup[]> {
  if (!animalGroupIds || animalGroupIds.length === 0) {
    return [];
  }

  // Animal groups are pre-fetched during initialization and rarely change.
  // Use a long staleTime to prevent unnecessary re-fetches when initialization
  // is invoked multiple times during app load.
  const ANIMAL_GROUP_STALE_TIME = 5 * 60 * 1000; // 5 minutes

  // Fetch via React Query to get in-flight dedupe + staleTime caching.
  // This prevents duplicate /v2/animalGroups/:id calls when farm init and hooks race.
  // The queryFn must apply processAnimalGroupResponse so the cache always holds the
  // processed value — matching useAnimalGroup, which shares this queryKey. Otherwise
  // a fresh cache entry from useAnimalGroup would be re-processed here and
  // postGrazingTarget would get multiplied by 10000 a second time.
  return await Promise.all(
    animalGroupIds.map((id) =>
      queryClient.fetchQuery({
        queryKey: queryKeys.animalGroups.detail(id),
        queryFn: async () => {
          const response = await animalGroupApiV2.getOne(id);
          return processAnimalGroupResponse([response])[0];
        },
        staleTime: ANIMAL_GROUP_STALE_TIME,
      })
    )
  );
}
